import type {
  PaymentService,
  IPaymentService,
  IPaymentAIOOfflinePayload,
  IPaymentAIOOfflineResponse,
  IBankAccount,
  IPaymentAIOOnlineResponse,
  IBank,
  IPaymentAIOOnlinePayload,
  IPaymentBffService,
  PaymentBffService,
  ICreatePaymentBffAIORequest,
  ICreatePaymentBffAIOResponse,
} from '@teko/payment';

interface ConstructorParams {
  baseUrl: string;
  paymentSettingUrl: string;
  secretKey: string;
}

class PaymentBffServiceWrapper implements IPaymentBffService {
  private servicePromise: Promise<PaymentBffService> = null;
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async initializeService() {
    if (!this.servicePromise) {
      const baseUrl = this.baseUrl;
      this.servicePromise = import('@teko/payment').then(
        (mod) => new mod.PaymentBffService(baseUrl),
      );
    }
    return this.servicePromise;
  }

  async createPaymentAIO(
    request: ICreatePaymentBffAIORequest,
  ): Promise<ICreatePaymentBffAIOResponse> {
    const service = await this.initializeService();
    return service.createPaymentAIO(request);
  }
}

class PaymentServiceWrapper implements IPaymentService {
  private servicePromise: Promise<PaymentService> = null;
  private constructorParams: ConstructorParams;

  constructor(params: ConstructorParams) {
    this.constructorParams = params;
  }

  private async initializeService() {
    if (!this.servicePromise) {
      const params = this.constructorParams;
      this.servicePromise = import('@teko/payment').then(
        (mod) =>
          new mod.PaymentService(
            params.baseUrl,
            params.secretKey,
            params.paymentSettingUrl,
          ),
      );
    }
    return this.servicePromise;
  }

  async initPaymentAIOOffline(
    body: Omit<IPaymentAIOOfflinePayload, 'checksum'>,
  ): Promise<IPaymentAIOOfflineResponse> {
    const service = await this.initializeService();
    return service.initPaymentAIOOffline(body);
  }

  async initPaymentAIOnline(
    body: Omit<IPaymentAIOOnlinePayload, 'checksum'>,
  ): Promise<IPaymentAIOOnlineResponse> {
    const service = await this.initializeService();
    return service.initPaymentAIOnline(body);
  }

  async getBanks(): Promise<IBank[]> {
    const service = await this.initializeService();
    return service.getBanks();
  }

  async getBankAccounts(): Promise<IBankAccount[]> {
    const service = await this.initializeService();
    return service.getBankAccounts();
  }

  async requestCreateToken(
    merchantCode: string,
    userId: string,
    cartType: number,
    successUrl: string,
    cancelUrl: string,
  ) {
    const service = await this.initializeService();
    return service.requestCreateToken(
      merchantCode,
      userId,
      cartType,
      successUrl,
      cancelUrl,
    );
  }

  async requestRemoveToken(
    merchantMethodCode: string,
    userId: string,
    token: string,
  ) {
    const service = await this.initializeService();
    return service.requestRemoveToken(merchantMethodCode, userId, token);
  }
}

export { PaymentServiceWrapper, PaymentBffServiceWrapper };
